import { render } from 'react-dom';
import { CssBaseline } from '@mui/material';
import { setConfig } from 'react-hot-loader';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider, Theme, StyledEngineProvider } from '@mui/material/styles';

import Root from './components/root';
import packageJson from '../package.json';
import interceptors from './interceptors';
import { commonTheme } from './styles/themes';
import { nortadaLogger } from './components/common/logger';
import { authentication } from './components/authentication';
import SentryAdapter from './components/common/logger/sentryAdapter';
import ConsoleAdapter from './components/common/logger/consoleAdapter';

declare module '@mui/styles/defaultTheme' {
  interface DefaultTheme extends Theme {}
}

declare global {
  interface Window {
    nortada: {
      keycloakAuthentication: {
        realm: string;
        clientId: string;
        url: string;
        redirectUrl: string;
      };
      logger: {
        dest: 'console' | string;
      };
      nortadaSettings: {
        clientBalanceRefreshTime: number;
      };
      api: {
        client: string;
        guide: string;
        doc: string;
      };
    };
  }
}

function loadConfigFile() {
  let checkIfConfigLoaded: number;
  let noConfigLoaded: number;

  return new Promise((resolve, reject) => {
    const { clearInterval, setInterval, setTimeout, clearTimeout } = window;

    checkIfConfigLoaded = setInterval(() => {
      if (window.nortada) {
        clearInterval(checkIfConfigLoaded);
        clearTimeout(noConfigLoaded);
        resolve(true);
      }
    }, 100);
    noConfigLoaded = setTimeout(() => {
      reject(new Error('Config file not loaded'));
    }, 3000);
  }).catch(message => {
    nortadaLogger.error(message);
    clearInterval(checkIfConfigLoaded);
  });
}

function initReactApp() {
  render(
    <StyledEngineProvider injectFirst={true}>
      <ThemeProvider theme={commonTheme}>
        <CssBaseline />
        <BrowserRouter basename="portal">
          <Root />
        </BrowserRouter>
      </ThemeProvider>
    </StyledEngineProvider>,
    document.getElementById('root')
  );
}

setConfig({ pureSFC: true });

function getLogger() {
  const loggerDestination = window.nortada?.logger?.dest;

  if (!loggerDestination || loggerDestination === 'console') {
    return new ConsoleAdapter();
  }

  return new SentryAdapter(loggerDestination);
}

loadConfigFile().then(() => {
  nortadaLogger.settings(getLogger());
  nortadaLogger.info(`=== ${packageJson.name} ${packageJson.version} ===`);
  authentication.verifyAuthProviderConnection(
    () => {
      interceptors.use();
      initReactApp();
    },
    message => {
      // TODO: The way haw we show problem with Keycloak connection should be done together with QRIOS-4718
      if (typeof message === 'string') {
        nortadaLogger.error(message);
      }
    }
  );
});
